import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AuditContent } from './AuditPage';
import axios from 'axios';
import { Plus, Edit, Trash2, User, Shield, Check, X as XIcon, Users as UsersIcon, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { email: '', password: '', name: '', role: 'caixa', permissions: [], active: true };

export const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [mainTab, setMainTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [uRes, pRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/users/permissions/available`, { withCredentials: true })
      ]);
      setUsers(uRes.data);
      setPermissions(pRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar usuários');
    }
  };

  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const selectAllInGroup = (group, allKeys, allSelected) => {
    setFormData(prev => {
      const set = new Set(prev.permissions);
      if (allSelected) {
        allKeys.forEach(k => set.delete(k));
      } else {
        allKeys.forEach(k => set.add(k));
      }
      return { ...prev, permissions: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = { ...formData };
      if (editingId) {
        if (!payload.password) delete payload.password;
        await axios.put(`${API}/users/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/users`, payload, { withCredentials: true });
      }
      load();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (u) => {
    setFormData({
      email: u.email,
      password: '',
      name: u.name,
      role: u.role,
      permissions: u.permissions || [],
      active: u.active !== false
    });
    setEditingId(u.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      await axios.delete(`${API}/users/${id}`, { withCredentials: true });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  // Agrupar permissões
  const permsByGroup = permissions.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  const roleColor = (role) => ({
    admin: 'bg-red-100 text-red-700 border-red-200',
    gerente: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    caixa: 'bg-blue-100 text-blue-700 border-blue-200'
  }[role] || 'bg-slate-100 text-slate-700');

  return (
    <Layout>
      <div className="p-8" data-testid="users-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight">Usuários & Auditoria</h1>
            <p className="text-base text-slate-600 mt-2">Gerencie usuários, permissões e acompanhe o log de auditoria</p>
          </div>
        </div>

        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="grid w-full md:w-[400px] grid-cols-2 mb-6">
            <TabsTrigger value="users" data-testid="tab-users">
              <UsersIcon size={16} className="mr-2" /> Usuários
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <ShieldCheck size={16} className="mr-2" /> Auditoria
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="flex items-center justify-end mb-4">
              <Button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
                <Plus size={20} className="mr-2" /> Novo Usuário
              </Button>
            </div>

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{editingId ? 'Editar Usuário' : 'Novo Usuário'}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="user-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={!!editingId} data-testid="user-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>{editingId ? 'Nova Senha (deixe vazio para não alterar)' : 'Senha *'}</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingId} minLength={6} data-testid="user-password-input" />
                </div>
                <div className="space-y-2">
                  <Label>Papel *</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="gerente">Gerente</SelectItem>
                      <SelectItem value="caixa">Caixa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingId && (
                  <div className="space-y-2 md:col-span-2 flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="active"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-5 h-5"
                      data-testid="user-active-toggle"
                    />
                    <Label htmlFor="active" className="cursor-pointer">Usuário ativo (pode fazer login)</Label>
                  </div>
                )}
              </div>

              {/* Permissions */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={20} className="text-blue-600" />
                  <h3 className="font-bold text-slate-900">Permissões</h3>
                </div>
                {formData.role === 'admin' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-sm text-blue-900 font-medium">⚡ Administradores possuem todas as permissões automaticamente.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(permsByGroup).map(([group, perms]) => {
                      const groupKeys = perms.map(p => p.key);
                      const allSelected = groupKeys.every(k => formData.permissions.includes(k));
                      const someSelected = groupKeys.some(k => formData.permissions.includes(k));
                      return (
                        <div key={group} className="border border-slate-200 rounded-md p-3">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-sm text-slate-700 uppercase tracking-wider">{group}</h4>
                            <button
                              type="button"
                              onClick={() => selectAllInGroup(group, groupKeys, allSelected)}
                              className={`text-xs font-medium ${allSelected ? 'text-red-600' : 'text-blue-600'} hover:underline`}
                            >
                              {allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {perms.map(p => {
                              const checked = formData.permissions.includes(p.key);
                              return (
                                <label
                                  key={p.key}
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${checked ? 'bg-blue-50 border border-blue-200' : 'border border-transparent hover:bg-slate-50'}`}
                                  data-testid={`perm-${p.key}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => togglePermission(p.key)}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm text-slate-700">{p.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">
                  {loading ? 'Salvando...' : 'Salvar Usuário'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Usuário</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Email</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Papel</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Permissões</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nenhum usuário</td></tr>}
              {users.map(u => (
                <tr key={u.id} className={`hover:bg-slate-50 ${u.active === false ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-md border font-bold uppercase tracking-wider ${roleColor(u.role)}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {u.role === 'admin' ? (
                      <span className="text-red-600 font-medium">Todas (admin)</span>
                    ) : (
                      <span>{(u.permissions || []).length} permissão(ões)</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.active === false ? (
                      <span className="text-xs px-2 py-1 rounded bg-slate-200 text-slate-700 font-medium flex items-center gap-1 w-fit"><XIcon size={12} />Inativo</span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-medium flex items-center gap-1 w-fit"><Check size={12} />Ativo</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(u)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-user-${u.id}`}><Edit size={16} /></button>
                      {u.id !== currentUser?.id && u.email !== 'admin@mercado.com' && (
                        <button onClick={() => handleDelete(u.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </TabsContent>

          <TabsContent value="audit">
            <AuditContent />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
